
import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { Helmet } from "react-helmet";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardHeader, CardDescription, CardTitle, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Info } from "lucide-react";
import ClaimsTable from "@/components/admin/ClaimsTable";
import ClaimDetailsCard from "@/components/admin/ClaimDetailsCard";
import { getStatusBadge } from "@/components/admin/ClaimsUtils";
import { NursingHomeClaim } from "@/types/admin";

type Json = string | number | boolean | null | { [key: string]: Json } | Json[];

export default function ClaimsManagementPage() {
  const [claims, setClaims] = useState<NursingHomeClaim[]>([]);
  const [selectedClaim, setSelectedClaim] = useState<NursingHomeClaim | null>(null);
  const [loading, setLoading] = useState(true);
  const [adminNotes, setAdminNotes] = useState("");
  const [activeTab, setActiveTab] = useState("pending");

  const fetchClaims = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("nursing_home_claims_view")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Transform the data to match our expected format
      const typedData = (data || []).map((claim: any) => {
        const verificationDocs = claim.verification_documents
          ? typeof claim.verification_documents === "object"
            ? claim.verification_documents
            : {}
          : null;
        
        // Create user object from the view data
        const user = claim.user_email ? {
          email: claim.user_email,
          user_metadata: {
            first_name: claim.user_first_name,
            last_name: claim.user_last_name
          }
        } : null;
        
        // Create nursing home object from the view data
        const nursing_home = claim.center_name ? {
          center_name: claim.center_name,
          county: claim.county
        } : null;
        
        return {
          ...claim,
          verification_documents: verificationDocs,
          user,
          nursing_home,
        } as NursingHomeClaim;
      });

      setClaims(typedData);
    } catch (error) {
      console.error("Error fetching claims:", error);
      toast.error("Failed to load claim requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClaims();
  }, []);

  const handleApproval = async (approved: boolean) => {
    if (!selectedClaim) return;
    try {
      const status = approved ? "approved" : "rejected";
      const { error: updateError } = await supabase
        .from("nursing_home_claims")
        .update({
          status,
          notes: adminNotes,
          verified_by: (await supabase.auth.getSession()).data.session?.user.id,
          verified_at: new Date().toISOString(),
        })
        .eq("id", selectedClaim.id);

      if (updateError) throw updateError;

      if (approved) {
        const { error: nursingHomeError } = await supabase
          .from("nursing_homes")
          .update({
            claimed: true,
            claimed_by: selectedClaim.user_id,
            claimed_at: new Date().toISOString(),
          })
          .eq("nh_slug", selectedClaim.nursing_home_id);

        if (nursingHomeError) throw nursingHomeError;
      }

      toast.success(`Claim ${approved ? "approved" : "rejected"} successfully`);
      setSelectedClaim(null);
      fetchClaims();
    } catch (error) {
      console.error(
        `Error ${approved ? "approving" : "rejecting"} claim:`,
        error,
      );
      toast.error(`Failed to ${approved ? "approve" : "reject"} claim`);
    }
  };

  // Utilities
  const getFilteredClaims = () =>
    claims.filter(claim => claim.status === activeTab);

  return (
    <AdminLayout>
      <Helmet>
        <title>Claims Management | Grandpal Admin</title>
      </Helmet>
      <div className="container py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Nursing Home Claims</h1>
            <p className="text-muted-foreground">Review and manage profile ownership claims</p>
          </div>
          <Button variant="outline" onClick={fetchClaims} disabled={loading}>
            {loading ? "Loading..." : "Refresh"}
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="pending">
              Pending
              <span className="ml-2">
                {claims.filter(c => c.status === "pending").length}
              </span>
            </TabsTrigger>
            <TabsTrigger value="approved">Approved</TabsTrigger>
            <TabsTrigger value="rejected">Rejected</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="space-y-4">
            {loading ? (
              <div className="py-8 text-center">Loading claims...</div>
            ) : getFilteredClaims().length === 0 ? (
              <div className="py-8 text-center border rounded-md bg-muted/10">
                <div className="flex justify-center mb-2">
                  <Info className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium">No {activeTab} claims</h3>
                <p className="text-muted-foreground">
                  There are no claims with this status.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="col-span-1 md:col-span-2">
                  <Card>
                    <CardHeader className="px-6">
                      <CardTitle>Claim Requests</CardTitle>
                      <CardDescription>
                        Showing {getFilteredClaims().length} {activeTab} claim
                        {getFilteredClaims().length !== 1 ? "s" : ""}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="px-6">
                      <ClaimsTable
                        claims={claims}
                        activeTab={activeTab}
                        onViewDetails={setSelectedClaim}
                      />
                    </CardContent>
                  </Card>
                </div>
                {selectedClaim && (
                  <ClaimDetailsCard
                    claim={selectedClaim}
                    adminNotes={adminNotes}
                    setAdminNotes={setAdminNotes}
                    onApprove={() => handleApproval(true)}
                    onReject={() => handleApproval(false)}
                    onClose={() => setSelectedClaim(null)}
                  />
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
